export const SERVICES_DATA = {
  "hotel-operations-consultancy": {
    slug: "hotel-operations-consultancy",
    title: "Hotel Operations Consultancy",
    metaTitle: "Hotel Operations Consultancy UK | Optimum Hospitality Solutions",
    metaDescription: "Hotel operations consultancy for UK hotel owners, general managers and operators. Practical, hands on support across rooms, food and beverage, labour and guest experience.",
    h1: "Hotel Operations Consultancy",
    overline: "Operational support for independent hotels",
    intro: "Operational consulting for UK independent hotels, boutique groups and serviced accommodation operators, led personally by Sidney Adams. We work alongside your team in the property to lift performance, consistency and profitability, with the discipline of someone who has run the operation themselves.",
    challenges: [
      "Service delivery and guest experience that varies between shifts and departments",
      "Labour productivity below benchmark, with rising costs and unclear ownership",
      "Standards drift, with SOPs out of date, audits failing and brand standards slipping",
      "Operational silos between rooms, food and beverage and back of house",
      "General managers stretched thin without an operating cadence to lean on",
    ],
    benefits: [
      "A measurable lift in operational margin, without sacrificing service",
      "A repeatable operating cadence that holds after the engagement closes",
      "Clearer accountability across HODs and the wider property team",
      "Improved guest scores and reputation positioning within a quarter",
      "A credible operating story for owners, lenders and acquirers",
    ],
    approach: [
      { n: "01", t: "Diagnose", b: "Time in the property reviewing operations, team, standards and the P&L. Findings prioritised by impact." },
      { n: "02", t: "Recommend", b: "Written recommendations with sequenced actions and indicative GOP impact ranges." },
      { n: "03", t: "Implement", b: "Delivery in the property alongside your team. Done with the team, not to the team." },
      { n: "04", t: "Embed", b: "Handover documentation, operating cadence and baseline measurement that lasts after we leave." },
    ],
    why: [
      "Led personally by Sidney Adams, with over a decade of hospitality leadership in the property",
      "Practical advice from someone who has run the operation, not slide deck consultancy",
      "Independent of any operating brand, with recommendations made in the owner's interest",
      "UK wide, with meaningful time spent in the property at every engagement",
    ],
    faqs: [
      { q: "What size of hotel do you typically work with?", a: "Independent hotels of 25 to 200 keys, including boutique and country house properties. Asset values typically £5M to £50M." },
      { q: "Will you take over my general manager or management team?", a: "No. Our delivery model supports your existing team. We work alongside the property leadership, not over them." },
      { q: "How long is a typical engagement?", a: "Most engagements are 13 weeks (the 90 day performance reset). Shorter diagnostics and longer advisory partnerships are also available." },
      { q: "What does it cost?", a: "Investment is scoped to the property and the brief. As a guide, a 90 day performance reset typically sits between £15,000 and £45,000." },
      { q: "Do you work UK wide?", a: "Yes. Based in London, working with hotel owners and operators across England, Scotland, Wales and Northern Ireland." },
    ],
    relatedSlugs: ["hospitality-business-performance-improvement", "hospitality-audits-operational-reviews", "labour-productivity-cost-optimisation"],
    keywords: "hotel operations consultancy uk, hotel operations consultant london, independent hotel operations, hotel performance consulting",
  },

  "hospitality-business-performance-improvement": {
    slug: "hospitality-business-performance-improvement",
    title: "Hospitality Business Performance Improvement",
    metaTitle: "Hospitality Business Performance Improvement UK | Optimum",
    metaDescription: "Performance improvement consulting for UK hospitality businesses. Practical work that lifts GOP, guest experience and team effectiveness across hotels, aparthotels and serviced accommodation.",
    h1: "Hospitality Business Performance Improvement",
    overline: "Moving the operating P&L",
    intro: "Performance improvement for hospitality businesses where the numbers have plateaued or declined. We diagnose where the GOP is leaking and rebuild the operating layer to capture it, without rebranding, restructuring or replacing the team.",
    challenges: [
      "Revenue rising but GOP flat or in decline",
      "Cost lines drifting without clear operational ownership",
      "Pricing and channel mix that is not optimised for the asset",
      "Operational leakage across labour, food and beverage and reservation conversion",
      "Top line growth that struggles to translate into bottom line contribution",
    ],
    benefits: [
      "£200k or more of incremental GOP typically delivered in the first 12 months",
      "Sharper rate, channel and cost discipline",
      "A measurable lift in EBITDA and operating contribution",
      "A performance cadence the general manager and HODs can run after we leave",
      "An evidence base for refinancing, valuation or sale events",
    ],
    approach: [
      { n: "01", t: "P&L diagnostic", b: "An operational read of the management accounts. Where the money is, and where it is leaking." },
      { n: "02", t: "Lever map", b: "Prioritised levers ranked by speed to impact, P&L size and operational risk." },
      { n: "03", t: "Delivery in the property", b: "Implementation alongside the general manager and HODs, not in parallel to them." },
      { n: "04", t: "Measure and sustain", b: "Baseline, cadence and KPIs that keep performance moving after we go." },
    ],
    why: [
      "Delivered personally by a hospitality operations leader with real in property credibility",
      "Led by the numbers, grounded in evidence, never a theoretical playbook",
      "Independent of operating brands, with no operating company conflict",
      "Engagements scoped to outcome, not to billable hours",
    ],
    faqs: [
      { q: "What kind of uplift should I realistically expect?", a: "Engagements typically deliver £200k or more of incremental GOP within 12 months on independent properties of 50 keys and above. Ranges vary by asset and starting position." },
      { q: "How quickly will we see results?", a: "Most engagements produce measurable operational change from week six. GOP movement is typically visible in the management accounts within one to two quarters." },
      { q: "Will my operator or brand resist this?", a: "We work alongside operators and brands respectfully. Most welcome a structured, external review of the operating layer grounded in evidence." },
      { q: "Is this only for hotels?", a: "No. We work across hotels, aparthotels, serviced apartments and hospitality groups." },
    ],
    relatedSlugs: ["hotel-operations-consultancy", "labour-productivity-cost-optimisation", "hospitality-audits-operational-reviews"],
    keywords: "hospitality business performance, hotel performance improvement, hotel GOP improvement, hospitality profitability consulting uk",
  },

  "labour-productivity-cost-optimisation": {
    slug: "labour-productivity-cost-optimisation",
    title: "Labour Productivity & Cost Optimisation",
    metaTitle: "Hotel Labour Productivity & Cost Optimisation UK | Optimum",
    metaDescription: "Labour productivity and cost work for UK hotels and hospitality operators. Reset the labour line without losing service standards.",
    h1: "Labour Productivity & Cost Optimisation",
    overline: "The labour line, properly run",
    intro: "Labour is the single biggest controllable cost line in most hospitality operations, and the most commonly mismanaged. We rebuild the labour model around demand, role design and productivity benchmarks, then embed the discipline so it sustains.",
    challenges: [
      "Labour cost rising faster than revenue",
      "Rostering driven by habit rather than demand",
      "Productivity benchmarks that are unclear or unmeasured",
      "Role overlap, gaps and inefficient handovers",
      "Standards slipping when the labour line is squeezed in the wrong places",
    ],
    benefits: [
      "Typical labour cost reduction of 80 to 250bps without service degradation",
      "Rosters built around demand, protecting the guest moments that matter",
      "Clear productivity benchmarks by department",
      "Reduced reliance on agency and overtime",
      "Better retention through well designed roles",
    ],
    approach: [
      { n: "01", t: "Labour audit", b: "Department by department review of rosters, productivity, role design and cost." },
      { n: "02", t: "Demand model", b: "Rebuild rosters and structures against actual demand, not historical habit." },
      { n: "03", t: "Implement", b: "Roll out new rosters, role descriptions and cadences alongside HODs." },
      { n: "04", t: "Measure", b: "Track productivity, cost and guest scores against baseline. Adjust monthly." },
    ],
    why: [
      "A decade of in property experience designing and running labour models",
      "Practical, and scalable from a single site to a small group",
      "Service first, with labour discipline never at the expense of guest experience",
      "Aligned to the owner, not to an outsourced labour provider's commercial interest",
    ],
    faqs: [
      { q: "Will this lead to redundancies?", a: "Not usually. Most labour gains come from role design, rostering and overtime reduction, not headcount cuts." },
      { q: "How long until labour cost actually moves?", a: "New rosters typically take effect within 30 days. Cost movement is visible within the first full payroll cycle after rollout." },
      { q: "Does this work for food and beverage and back of house?", a: "Yes. The methodology applies across rooms division, food and beverage, kitchen, housekeeping and back of house." },
    ],
    relatedSlugs: ["hospitality-business-performance-improvement", "hotel-operations-consultancy", "hospitality-audits-operational-reviews"],
    keywords: "hotel labour productivity, hospitality cost optimisation, hotel labour cost reduction uk, hotel rostering consultant",
  },

  "guest-experience-enhancement": {
    slug: "guest-experience-enhancement",
    title: "Guest Experience Enhancement",
    metaTitle: "Hotel Guest Experience Consulting UK | Optimum Hospitality Solutions",
    metaDescription: "Guest experience consulting for UK hotels and hospitality operators. Lift TripAdvisor, Booking.com and brand standard scores through operational discipline, not slogans.",
    h1: "Guest Experience Enhancement",
    overline: "Service standards that actually move scores",
    intro: "Guest experience is built operationally, not on a values poster in the corridor. We rebuild the guest journey from arrival to departure with the SOPs, training and cadence that translate into a measurable lift in reputation within one quarter.",
    challenges: [
      "Guest scores below the competitor set despite team effort",
      "Service that varies between shifts, departments and channels",
      "Complaint volume rising, or repeat custom declining",
      "Brand standards drifting without an audit cadence",
      "Operational issues being misdiagnosed as a service training problem",
    ],
    benefits: [
      "A typical lift of 0.3 to 0.7 points on guest scores within a quarter",
      "A reduction in complaint volume and refund exposure",
      "Stronger reputation positioning against the competitor set",
      "Repeat custom and direct bookings rebuilt",
      "Team confidence and pride lifted alongside performance",
    ],
    approach: [
      { n: "01", t: "Guest journey audit", b: "End to end mapping of the guest experience, online, in the property and post stay." },
      { n: "02", t: "Standards rebuild", b: "Practical SOPs and brand standards, specific to your property, that your team will actually adopt." },
      { n: "03", t: "Train and launch", b: "Service training delivered with HODs, not over them. Standards embedded into the operation." },
      { n: "04", t: "Audit cadence", b: "A monthly quality programme that keeps standards honest after the engagement closes." },
    ],
    why: [
      "Service standards built by someone who has run them in the property",
      "SOPs your team will read, follow and respect, not corporate boilerplate",
      "Measurable, not aspirational",
      "An independent perspective on reputation positioning",
    ],
    faqs: [
      { q: "How fast can scores actually move?", a: "Most engagements show measurable score movement within one quarter, with sustained lift over two to three quarters." },
      { q: "Do you train the team directly?", a: "We design and deliver service training alongside HODs and the general manager, with materials and cadences your team can re-run." },
      { q: "What about online reputation management?", a: "Yes. We cover review response standards, the operating cadence for managing reputation, and the underlying operational fixes that prevent recurring complaints." },
    ],
    relatedSlugs: ["hotel-operations-consultancy", "hospitality-audits-operational-reviews", "multi-site-hospitality-performance-support"],
    keywords: "hotel guest experience consultant, hospitality guest experience uk, hotel reputation management uk, hotel service standards",
  },

  "hotel-pre-opening-operational-setup": {
    slug: "hotel-pre-opening-operational-setup",
    title: "Hotel Pre-Opening & Operational Setup",
    metaTitle: "Hotel Pre-Opening Consultant UK | Optimum Hospitality Solutions",
    metaDescription: "Pre-opening hospitality consulting for new build hotels, repositioning projects and aparthotel launches across the UK. Build the operating spine before day one.",
    h1: "Hotel Pre-Opening & Operational Setup",
    overline: "Get day one right",
    intro: "Pre-opening support for new build hotels, aparthotels, boutique launches and major repositions. We build the operating spine, SOPs, team structure, standards and opening operating plan, so day one is a controlled launch rather than a recovery exercise.",
    challenges: [
      "No operating spine in place ahead of opening",
      "Team being hired faster than the systems can absorb them",
      "Standards undefined, or borrowed from another property",
      "Pre-opening budget under pressure with no clear operational priorities",
      "Owner or developer needing operational visibility into an opening they are not running",
    ],
    benefits: [
      "Opened on time, on budget, with the operating layer ready to scale",
      "SOPs and standards in place before the first guest arrives",
      "A team trained against documented expectations, not improvised culture",
      "Documented operational evidence for lenders and brand partners",
      "Reduced first year guest score volatility and complaint exposure",
    ],
    approach: [
      { n: "01", t: "Pre-opening plan", b: "Operating workstreams, sequence, budget and decision points from breaking ground to opening day." },
      { n: "02", t: "Standards build", b: "SOPs specific to your property across rooms, food and beverage, housekeeping, front office and back of house." },
      { n: "03", t: "Team build", b: "Organisation structure, role descriptions, hiring sequence and induction programme." },
      { n: "04", t: "Soft launch and embed", b: "Soft opening cadence, snagging, audit programme and day 90 performance baseline." },
    ],
    why: [
      "Real opening experience inside the property, not a template library",
      "Advice on the owner's side, independent of brand or operating partner pressure",
      "Pragmatic phasing for the budget and team you actually have",
      "Coverage from groundwork through to day 90",
    ],
    faqs: [
      { q: "How early should we engage you?", a: "Ideally 9 to 12 months before opening. We can engage later, although the value of pre-opening support compounds with lead time." },
      { q: "Do you work alongside our brand or management company?", a: "Yes. We complement brand standards and operating partners, we do not replace them. Most brands welcome a structured advisor on the owner's side during pre-opening." },
      { q: "What if the project is a reposition rather than a new build?", a: "The same methodology applies. The operating spine is rebuilt around the new positioning, with a phased re-launch alongside continued trading." },
    ],
    relatedSlugs: ["hotel-operations-consultancy", "hospitality-audits-operational-reviews", "interim-hospitality-management-support"],
    keywords: "hotel pre-opening consultant uk, hotel opening support, new hotel launch consultant, aparthotel pre-opening",
  },

  "hospitality-audits-operational-reviews": {
    slug: "hospitality-audits-operational-reviews",
    title: "Hospitality Audits & Operational Reviews",
    metaTitle: "Hospitality Operational Audit UK | Optimum Hospitality Solutions",
    metaDescription: "Independent operational audits and reviews for UK hotels, aparthotels and hospitality groups. Findings grounded in evidence, with prioritised recommendations delivered within three weeks.",
    h1: "Hospitality Audits & Operational Reviews",
    overline: "Independent, grounded in evidence, fast",
    intro: "Structured operational audits for owners, asset managers and operators who need an independent read on a property's operating health. Delivered within three weeks with prioritised findings, indicative P&L impact ranges and a clear action plan.",
    challenges: [
      "Owner or asset manager unsure whether the operating team is performing to potential",
      "An acquisition or refinancing decision that needs evidence from the operating side",
      "An internal team too close to the operation to see the leaks",
      "Brand standards that are inconsistent across a portfolio",
      "A pre-sale operating story that needs tightening for buyers and lenders",
    ],
    benefits: [
      "Independent findings, fit for a board, within three weeks",
      "Prioritised recommendations with indicative GOP impact ranges",
      "An evidence base for refinancing, sale or board reporting",
      "Operational visibility for owners who are not running the property day to day",
      "A foundation for any subsequent improvement engagement",
    ],
    approach: [
      { n: "01", t: "Scope", b: "Confirm objectives, asset profile, audit depth and reporting format with ownership." },
      { n: "02", t: "In the property", b: "One to two days in the property. Confidential interviews, P&L review and a standards walk through." },
      { n: "03", t: "Report", b: "Written report with prioritised findings, ratings and indicative P&L impact." },
      { n: "04", t: "Findings session", b: "60 minute findings session with ownership and, where appropriate, the operating team." },
    ],
    why: [
      "Findings reflect what is actually happening in the operation, not desk research",
      "Reporting format ready for a board pack",
      "Confidential and ready to sign an NDA from first contact",
      "Available as a single asset or portfolio engagement",
    ],
    faqs: [
      { q: "How long does an audit take?", a: "Typically three weeks from kick off to written report, including the time in the property." },
      { q: "What does an audit cost?", a: "£4,500 to £12,000 per asset, scoped to property size and report depth. Portfolio engagements are priced per asset with bulk pricing above three." },
      { q: "Will the operating team know we are doing this?", a: "That is your decision. Audits can be positioned to the team as external support, an operating review, or a confidential process led by ownership. We follow your lead." },
    ],
    relatedSlugs: ["hospitality-business-performance-improvement", "hotel-operations-consultancy", "multi-site-hospitality-performance-support"],
    keywords: "hospitality operational audit, hotel audit uk, hotel operations review, hospitality asset diagnostic",
  },

  "multi-site-hospitality-performance-support": {
    slug: "multi-site-hospitality-performance-support",
    title: "Multi-Site Hospitality Performance Support",
    metaTitle: "Multi-Site Hospitality Performance Consultant UK | Optimum",
    metaDescription: "Multi site operational and performance support for UK hospitality groups, aparthotel operators and small portfolios. Standardise performance without losing the operating identity of each site.",
    h1: "Multi-Site Hospitality Performance Support",
    overline: "Consistency across the portfolio",
    intro: "Operational and performance support for hospitality groups, aparthotel operators and small portfolios. We standardise the performance layer across sites, including SOPs, KPIs, cadence and reporting, without flattening the operating identity that makes each property distinct.",
    challenges: [
      "Performance varies across sites with no clear cross portfolio cadence",
      "KPIs measured differently by each property",
      "Brand standards drifting in some sites and not others",
      "Head office stretched too thin to drive performance in the properties",
      "Group operating layer not yet built to scale beyond five sites",
    ],
    benefits: [
      "A standardised KPI pack across the portfolio",
      "A performance cadence head office can actually run",
      "A lift in performance across the underperforming sites",
      "A group level evidence base for owners, boards and investors",
      "A scalable framework that supports the next five sites",
    ],
    approach: [
      { n: "01", t: "Portfolio diagnostic", b: "A cross site read on performance, standards and operating cadence. Identify the gap between the best and worst performing sites." },
      { n: "02", t: "Standards harmonisation", b: "SOPs and standards aligned across sites, with local variation specific to each property built in." },
      { n: "03", t: "KPI and cadence build", b: "Single KPI pack, monthly cadence and quarterly review structure for ownership." },
      { n: "04", t: "Lift the laggards", b: "Targeted intervention in the property at the two or three sites that are underperforming." },
    ],
    why: [
      "Led personally across multiple sites at once, with bandwidth deliberately constrained to maintain quality",
      "A scalable structure built for owner operators and small groups, not enterprise complexity",
      "Independent of any operating brand or management company",
      "A single point of accountability across the portfolio",
    ],
    faqs: [
      { q: "How many sites do you typically work across?", a: "Two to twelve sites. Larger portfolios are supported through a phased rollout, typically two to three assets in pilot before scaling." },
      { q: "Do you replace our head office team?", a: "No. We support head office bandwidth and bring rigour from the operating side to the performance layer." },
      { q: "Is this only for hotels?", a: "No. Aparthotel operators, serviced apartment portfolios, boutique groups and restaurant with rooms operators all engage us." },
    ],
    relatedSlugs: ["hospitality-business-performance-improvement", "hospitality-audits-operational-reviews", "interim-hospitality-management-support"],
    keywords: "multi-site hospitality consultant, hospitality group operations, aparthotel portfolio consultant uk, hospitality multi-site performance",
  },

  "interim-hospitality-management-support": {
    slug: "interim-hospitality-management-support",
    title: "Interim Hospitality Management Support",
    metaTitle: "Interim Hospitality Management UK | Optimum Hospitality Solutions",
    metaDescription: "Interim hospitality management and operating support for UK hotels, aparthotels and groups between permanent appointments, during turnarounds or pre-opening.",
    h1: "Interim Hospitality Management Support",
    overline: "Operating cover when you need it",
    intro: "Senior interim operational support for owners and groups between permanent appointments, through turnaround periods or during pre-opening. We provide stability and discipline on the operating side without the cost of a long term hire, and without the politics of an operating company takeover.",
    challenges: [
      "A general manager vacancy with no clear successor, and the property losing momentum",
      "Operating performance slipping during a leadership transition",
      "A turnaround period that needs experienced bandwidth on the operating side",
      "A pre-opening or post-opening phase that needs senior capacity in the property",
      "An owner needing temporary operating cover during a sale or refinancing",
    ],
    benefits: [
      "Stability on the operating side through a transition period",
      "Real bandwidth in the property, not advisory only support",
      "Standards and cadence maintained, or rebuilt, during the interim",
      "Clear handover documentation for the incoming permanent appointment",
      "Aligned to the owner, with no incentive to extend the engagement beyond what is needed",
    ],
    approach: [
      { n: "01", t: "Scope and deploy", b: "Define the interim brief, cadence in the property, decision rights and exit criteria with ownership." },
      { n: "02", t: "Delivery in the property", b: "Senior operating presence on property to the agreed cadence. HOD support, standards maintenance and performance management." },
      { n: "03", t: "Stabilise and develop", b: "Embed cadence, develop the second line of leadership, maintain or rebuild standards." },
      { n: "04", t: "Handover", b: "Documented handover to the permanent appointment with a 90 day forward operating plan." },
    ],
    why: [
      "Real operating experience, not the rotation of an interim consultant",
      "Engagement scoped to a defined window, not open ended billable hours",
      "Aligned to the owner, transparent, and structured to hand over cleanly",
      "Confidential from first contact, including positioning to the team",
    ],
    faqs: [
      { q: "How long are typical interim engagements?", a: "Three to six months is most common. Shorter, six to twelve weeks, for pre-opening cover or a specific transition. Longer engagements are scoped on request." },
      { q: "What level of seniority do you cover?", a: "Interim cover at Operating Manager, General Manager and Operations Director level. Delivered personally by Sidney Adams." },
      { q: "How quickly can you start?", a: "Most engagements start within two to four weeks of the brief being signed off, subject to current engagement load." },
    ],
    relatedSlugs: ["hotel-operations-consultancy", "hotel-pre-opening-operational-setup", "multi-site-hospitality-performance-support"],
    keywords: "interim hospitality management uk, interim hotel manager, interim general manager hotel, hospitality interim consultant",
  },
};

export const SERVICE_SLUGS = Object.keys(SERVICES_DATA);
